import { useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import toast from "react-hot-toast";
import DeleteModal from "../utils/DeleteModal";
import LoadingOverlay from "./LoadingOverlay";

const ChecklistDragReorder = ({ items, setItems, onUpdate, renderItem }) => {
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [dragResult, setDragResult] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const confirmReorder = async () => {
        if (!dragResult) return;
        setIsLoading(true);

        const { source, destination } = dragResult;
        const ordered = [...items].sort((a, b) => a.order - b.order);
        const [movedItem] = ordered.splice(source.index, 1);

        const before = ordered[destination.index - 1]?.order ?? 0;
        const after = ordered[destination.index]?.order ?? before + 2000;
        const newOrder = (before + after) / 2;

        const updatedItem = { ...movedItem, order: newOrder };
        ordered.splice(destination.index, 0, updatedItem);

        setItems(ordered);

        try {
            await onUpdate(updatedItem);
            toast.success("Checklist item reordered successfully");
            setIsLoading(false);
        } catch (err) {
            toast.error("Failed to reorder item");
            setIsLoading(false);
        }

        setIsConfirmModalOpen(false);
        setDragResult(null);
    };

    const onDragEnd = (result) => {
        if (!result.destination || result.source.index === result.destination.index)
            return;
        setDragResult(result);
        setIsConfirmModalOpen(true);
    };

    return (
        <>
            <LoadingOverlay isLoading={isLoading} />

            <DeleteModal
                isModalOpen={isConfirmModalOpen}
                setIsModalOpen={setIsConfirmModalOpen}
                title="Are you sure you want to move this item?"
                buttonText="Move"
                onRemove={confirmReorder}
            />

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                    {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            {[...items]
                                .sort((a, b) => a.order - b.order)
                                .map((item, index) => (
                                    <Draggable
                                        key={item.id}
                                        draggableId={item.id}
                                        index={index}
                                    >
                                        {(provided) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                            >
                                                {renderItem(item, index)}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </>
    );
};

export default ChecklistDragReorder;
